import React, { useState, useEffect } from 'react';
import { Calendar, ChevronLeft, ChevronRight, Check, X, AlertCircle, Users } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import api from '../lib/api';

const PRESENCE_TYPES = [
  { key: 'dortoir_matin', label: 'Dortoir Matin', icon: '🌅' },
  { key: 'petit_dejeuner', label: 'Petit-dej', icon: '🥐' },
  { key: 'dejeuner', label: 'Dejeuner', icon: '🍽️' },
  { key: 'etude_soir', label: 'Etude soir', icon: '📚' },
  { key: 'diner', label: 'Diner', icon: '🌙' },
  { key: 'dortoir_soir', label: 'Dortoir Soir', icon: '😴' },
];

function BoardingPresencePage() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [students, setStudents] = useState([]);
  const [presenceRecords, setPresenceRecords] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedType, setSelectedType] = useState('dortoir_matin');

  const formatDateStr = (d) => { const dd = new Date(d); return dd.getFullYear() + '-' + String(dd.getMonth() + 1).padStart(2, '0') + '-' + String(dd.getDate()).padStart(2, '0'); };
  const formatDateDisplay = (d) => { return new Date(d).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }); };

  useEffect(() => { fetchData(); }, [currentDate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const dateStr = formatDateStr(currentDate);
      const [s, p, sum] = await Promise.all([
        api.get('/students'),
        api.get('/boarding-presence', { params: { date: dateStr } }),
        api.get('/boarding-presence/daily-summary', { params: { date: dateStr } })
      ]);
      setStudents(s.data.filter(st => st.boarding_type === 'interne'));
      setPresenceRecords(p.data);
      setSummary(sum.data);
    } catch (e) { toast.error('Erreur'); }
    finally { setLoading(false); }
  };

  const handleMarkPresence = async (studentId, presenceType, status) => {
    try {
      const dateStr = formatDateStr(currentDate);
      await api.post('/boarding-presence', { student_id: studentId, date: dateStr, presence_type: presenceType, status: status, time_recorded: new Date().toISOString() });
      toast.success('Enregistre');
      fetchData();
    } catch (e) { toast.error('Erreur'); }
  };

  const handleMarkAllPresent = async (presenceType) => {
    const dateStr = formatDateStr(currentDate);
    try {
      for (const student of students) {
        await api.post('/boarding-presence', { student_id: student.id, date: dateStr, presence_type: presenceType, status: 'present', time_recorded: new Date().toISOString() });
      }
      toast.success('Tous marques presents');
      fetchData();
    } catch (e) { toast.error('Erreur'); }
  };

  const getStudentPresence = (studentId, presenceType) => presenceRecords.find(p => p.student_id === studentId && p.presence_type === presenceType);
  const selectedTypeInfo = PRESENCE_TYPES.find(t => t.key === selectedType);
  const currentSummary = summary?.presence_summary?.[selectedType];
  const addDays = (d, n) => { const r = new Date(d); r.setDate(r.getDate() + n); return r; };
  const isToday = formatDateStr(currentDate) === formatDateStr(new Date());

  return (
    <div className="space-y-6" data-testid="boarding-presence-page">
      <div><h1 className="text-2xl font-bold">Presence Internat</h1><p className="text-muted-foreground text-sm">Suivi des presences des internes</p></div>

      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <Button variant="outline" size="icon" className="rounded-full" onClick={() => setCurrentDate(addDays(currentDate, -1))}><ChevronLeft className="w-4 h-4" /></Button>
            <div className="text-center"><h2 className="text-lg font-semibold capitalize">{formatDateDisplay(currentDate)}</h2>{isToday && <Badge className="mt-1">Aujourd&apos;hui</Badge>}</div>
            <Button variant="outline" size="icon" className="rounded-full" onClick={() => setCurrentDate(addDays(currentDate, 1))}><ChevronRight className="w-4 h-4" /></Button>
          </div>
        </CardContent>
      </Card>

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          {PRESENCE_TYPES.map((type) => {
            const data = summary.presence_summary?.[type.key];
            const rate = data?.total > 0 ? Math.round((data?.present || 0) / data.total * 100) : 0;
            return (
              <Card key={type.key} className={'cursor-pointer transition-all ' + (selectedType === type.key ? 'border-primary ring-2 ring-primary/20' : '')} onClick={() => setSelectedType(type.key)}>
                <CardContent className="p-3 text-center">
                  <span className="text-xl">{type.icon}</span>
                  <p className="text-xs font-medium mt-1">{type.label}</p>
                  <p className="text-lg font-bold text-primary">{data?.present || 0}/{data?.total || 0}</p>
                  <p className={'text-xs ' + (rate >= 90 ? 'text-green-600' : rate >= 70 ? 'text-amber-600' : 'text-red-600')}>{rate}%</p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2"><span className="text-2xl">{selectedTypeInfo?.icon}</span>{selectedTypeInfo?.label}</CardTitle>
            <div className="flex items-center gap-2">
              {currentSummary && <div className="text-sm text-muted-foreground"><span className="text-green-600 font-medium">{currentSummary.present}</span> / <span className="text-red-600 font-medium">{currentSummary.absent}</span> / <span className="text-gray-400">{currentSummary.not_marked}</span></div>}
              <Button size="sm" className="rounded-full" onClick={() => handleMarkAllPresent(selectedType)}><Check className="w-4 h-4 mr-1" />Tous presents</Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (<div className="space-y-2">{[1,2,3,4,5].map(i => <Skeleton key={i} className="h-12" />)}</div>) : students.length === 0 ? (
            <div className="text-center py-8"><Users className="w-10 h-10 mx-auto mb-2 text-muted-foreground/50" /><p className="text-muted-foreground">Aucun interne inscrit</p></div>
          ) : (
            <div className="space-y-2">
              {students.map((student) => {
                const presence = getStudentPresence(student.id, selectedType);
                const bgClass = presence?.status === 'present' ? 'bg-green-50 border-green-200' : presence?.status === 'absent' ? 'bg-red-50 border-red-200' : presence?.status === 'excuse' ? 'bg-amber-50 border-amber-200' : 'bg-gray-50';
                return (
                  <div key={student.id} className={'flex items-center justify-between p-3 rounded-lg border ' + bgClass}>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center"><span className="font-bold text-primary">{student.first_name[0]}{student.last_name[0]}</span></div>
                      <div><p className="font-medium">{student.first_name} {student.last_name}</p><p className="text-xs text-muted-foreground">{student.matricule}</p></div>
                    </div>
                    <div className="flex items-center gap-2">
                      {presence ? <Badge className={presence.status === 'present' ? 'bg-green-100 text-green-700' : presence.status === 'absent' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}>{presence.status === 'present' ? 'Present' : presence.status === 'absent' ? 'Absent' : 'Excuse'}</Badge> : <Badge variant="outline">Non marque</Badge>}
                      <div className="flex gap-1">
                        <Button size="icon" variant={presence?.status === 'present' ? 'default' : 'outline'} className="h-8 w-8 rounded-full" onClick={() => handleMarkPresence(student.id, selectedType, 'present')}><Check className="w-4 h-4" /></Button>
                        <Button size="icon" variant={presence?.status === 'absent' ? 'destructive' : 'outline'} className="h-8 w-8 rounded-full" onClick={() => handleMarkPresence(student.id, selectedType, 'absent')}><X className="w-4 h-4" /></Button>
                        <Button size="icon" variant={presence?.status === 'excuse' ? 'secondary' : 'outline'} className="h-8 w-8 rounded-full" onClick={() => handleMarkPresence(student.id, selectedType, 'excuse')}><AlertCircle className="w-4 h-4" /></Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
export default BoardingPresencePage;
